import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis, faPenToSquare, faCircleMinus, faPlus, faArrowDownShortWide, faArrowDownWideShort, faSearch, faHandshake,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getRemoteParties, deleteRemoteParty } from "../../api/RemotePartiesOCPI";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField";  // Assuming there's an InputField component
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import enabledBadge from "../../components/CheckMarkBadge";
import { useTranslation } from "react-i18next";

const OcpiPartiesPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [parties, setParties] = useState([]);
  const [filteredParties, setFilteredParties] = useState([]);
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => { } });
  const navigate = useNavigate();
  const [partiesLoaded, setPartiesLoaded] = useState(false);

  useEffect(() => {
    handleGetOcpiParties();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  // Combined filtering and sorting logic into one useEffect
  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = parties;

      // Filtering based on search
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = parties.filter(party =>
          party.remotePartyPublicId.toLowerCase().includes(searchLower) ||
          party.remotePartyName.toLowerCase().includes(searchLower)
        );
      }

      // Sorting the filtered parties
      const sorted = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn]?.toString().toLowerCase();
        let bValue = b[sortColumn]?.toString().toLowerCase();

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setFilteredParties(sorted);
    };

    applyFilterAndSort();
  }, [search, parties, sortColumn, sortOrder]);

  const handleGetOcpiParties = async () => {
    try {
      const accessToken = await getAccessToken();
      const OcpiParties = await getRemoteParties(accessToken);

      setParties(OcpiParties);  // Set parties once fetched
      setFilteredParties(OcpiParties);  // Initially show all parties
      setPartiesLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };


  const handleDeleteParty = async (remotePartyPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.ocpiParty") }));
    try {
      const accessToken = await getAccessToken();
      await deleteRemoteParty(remotePartyPublicId, accessToken);
      const newParties = await getRemoteParties(accessToken);
      setParties(newParties);  // Update parties after deletion
      toast.success(t("toast.deleted", { entity: t("entity.ocpiParty") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.ocpiParty") }), { id: toastId });
      console.error(error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === 'asc' ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
    };


  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">

            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.ocpiParties")} loading={!partiesLoaded} />
            </div>

            <div className={`${!partiesLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("nav.ocpiParties")}</h2>
                <div className="flex gap-3">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => { navigate("/ocpi-party/handshake"); }}
                  >
                    <FontAwesomeIcon icon={faHandshake} /> {t("pages.handshake")}
                  </button>
                  <button className="btn btn-primary" onClick={() => { navigate(`/ocpi-party/create`) }}>
                    <FontAwesomeIcon icon={faPlus} /> {t("pages.createOcpiParty")}
                  </button>
                </div>
              </div>
              <div className="rounded-md shadow-md w-full p-4">
                <div className="flex w-full justify-end">
                  <div className="mb-2">
                    <InputField icon={faSearch} placeholder={t("search.party")} value={search} onChange={(v) => { setSearch(v.target.value) }} />
                  </div>
                </div>
                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('remotePartyPublicId')}>
                          {t("table.partyId")} {getSortIcon('remotePartyPublicId') && <FontAwesomeIcon icon={getSortIcon('remotePartyPublicId')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('remotePartyName')}>
                          {t("common.name")} {getSortIcon('remotePartyName') && <FontAwesomeIcon icon={getSortIcon('remotePartyName')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('customerName')}>
                          {t("table.customerName")} {getSortIcon('customerName') && <FontAwesomeIcon icon={getSortIcon('customerName')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('isOcpiHandshakeCompleted')}>
                            {t("table.handshakeCompleted")} {getSortIcon('isOcpiHandshakeCompleted') && <FontAwesomeIcon icon={getSortIcon('isOcpiHandshakeCompleted')} className="w-4 pl-2" />}
                        </th>    
                        <th className="text-center cursor-pointer" onClick={() => handleSort('versionNumber')}>
                            {t("table.versionNumber")} {getSortIcon('versionNumber') && <FontAwesomeIcon icon={getSortIcon('versionNumber')} className="w-4 pl-2" />}
                        </th>    
                        <th className="text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParties.map((party, index) => (
                        <tr key={index}>
                          <td className="cursor-pointer" onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}>{party.remotePartyPublicId}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}>{party.remotePartyName}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}>{party.customerName}</td>
                              <td className="cursor-pointer" onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}>{enabledBadge(party.isOcpiHandshakeCompleted)}</td>
                              <td className="cursor-pointer" onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}>{party.versionNumber}</td>
                          <td className="text-center flex justify-end">
                            <CustomDropdown isRight={true}
                              ContentComponent={() => {
                                return (
                                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { navigate(`/ocpi-party/${party.remotePartyPublicId}`) }}
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                    </button>
                                    <button
                                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { setConfirmState({ isOpen: true, title: t("confirm.deleteEntity", { entity: t("entity.ocpiParty"), name: party.remotePartyName }), confirm: () => { handleDeleteParty(party.remotePartyPublicId) } }) }}
                                    >
                                      <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                  </div>
                                );
                              }}>
                              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                              </div>
                            </CustomDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
        onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
      />
    </>
  );
};

export default OcpiPartiesPage;
